import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/feedback/Toaster';
import * as dqsApi from '../dqs.api';
import type { CandidateRule } from '../dqs.api';

export function useCompiledRules() {
  return useQuery({
    queryKey: ['dqs', 'rules', 'compiled'],
    queryFn: () => dqsApi.getCompiledRules(),
  });
}

export function useRuleDetail(ruleId: string | null) {
  return useQuery({
    queryKey: ['dqs', 'rules', ruleId],
    queryFn: () => dqsApi.getRuleDetail(ruleId!),
    enabled: !!ruleId,
  });
}

export function useToggleAutoCorrection() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: string; enabled: boolean; fieldName?: string }) =>
      dqsApi.toggleAutoCorrection(ruleId, enabled),

    onMutate: async ({ ruleId, enabled }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['dqs', 'rules', 'compiled'] });

      // Snapshot previous value
      const previousData = queryClient.getQueryData<{ data: CandidateRule[] }>(['dqs', 'rules', 'compiled']);

      // Optimistic update
      queryClient.setQueryData<{ data: CandidateRule[] }>(
        ['dqs', 'rules', 'compiled'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((rule) =>
              (rule._id === ruleId || rule.patternKey === ruleId)
                ? { ...rule, autoCorrectionEnabled: enabled }
                : rule,
            ),
          };
        },
      );

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      // Rollback
      if (context?.previousData) {
        queryClient.setQueryData(['dqs', 'rules', 'compiled'], context.previousData);
      }
      addToast({ key: 'dqs.autoCorrection.updateError' }, 'error');
    },

    onSuccess: (_data, { enabled, fieldName }) => {
      addToast(
        {
          key: 'dqs.autoCorrection.updateSuccess',
          values: {
            status: { key: enabled ? 'dqs.autoCorrection.enabled' : 'dqs.autoCorrection.disabled' },
            fieldName: fieldName || { key: 'dqs.rule' },
          },
        },
        'success',
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dqs', 'rules', 'compiled'] });
    },
  });
}
